import React from 'react'

import { PRMarinaLayerType } from '@sketch-hq/sketch-web-renderer'
// Both from the inspector, which is a declared dependency here and re-exports
// the scene model's own type - scene-model itself is not one.
import { ElementIcon, ElementType } from '@sketch/inspector'

import { EntityChange, LayerChange } from './layerTypes'

/**
 * The renderer's layer kinds as `ElementType`, which is what draws an icon.
 * Written out rather than cast, because a silent mismatch shows the wrong icon.
 *
 * Built on first use rather than at module scope: `PRMarinaLayerType` is a
 * value, and reading it while this module loads breaks tests that mock the
 * renderer package for something else.
 */
let elementTypes: Partial<Record<PRMarinaLayerType, ElementType>> | undefined

const elementTypeFor = (type: number) => {
  elementTypes ??= {
    [PRMarinaLayerType.Artboard]: ElementType.Artboard,
    [PRMarinaLayerType.SymbolMaster]: ElementType.SymbolMaster,
    [PRMarinaLayerType.SymbolInstance]: ElementType.SymbolInstance,
    [PRMarinaLayerType.Hotspot]: ElementType.Hotspot,
    [PRMarinaLayerType.Bitmap]: ElementType.Bitmap,
    [PRMarinaLayerType.Group]: ElementType.Group,
    [PRMarinaLayerType.Oval]: ElementType.Oval,
    [PRMarinaLayerType.Polygon]: ElementType.Polygon,
    [PRMarinaLayerType.Rectangle]: ElementType.Rectangle,
    [PRMarinaLayerType.ShapeGroup]: ElementType.ShapeGroup,
    [PRMarinaLayerType.ShapePath]: ElementType.ShapePath,
    [PRMarinaLayerType.Star]: ElementType.Star,
    [PRMarinaLayerType.Text]: ElementType.Text,
    [PRMarinaLayerType.Triangle]: ElementType.Triangle,
    [PRMarinaLayerType.Slice]: ElementType.Slice,
    [PRMarinaLayerType.Section]: ElementType.Section,
  }

  return elementTypes[type as PRMarinaLayerType]
}

/**
 * The icon a changed layer is drawn with.
 *
 * The canvas's own `ElementIcon`, not a set of our own: a text layer in this
 * list should look like a text layer everywhere else, and a second mapping
 * would drift from the first the moment either gained a kind.
 *
 * Falls back to the shape icon for a layer whose kind the reading did not
 * record, which is what `ElementIcon` does for anything it does not recognise -
 * a generic mark rather than a gap where the other rows have one.
 */
export function LayerChangeIcon({ change }: { change: LayerChange }) {
  const elementType =
    change.type === null
      ? ElementType.Undefined
      : (elementTypeFor(change.type) ?? ElementType.Undefined)

  return (
    <ElementIcon
      elementType={elementType}
      isLayerMasked={false}
      isLayerExportable={false}
      isVariantSet={false}
    />
  )
}

/**
 * The icon a changed entity is drawn with.
 *
 * A frame or a Section, which the comparison already knows without consulting
 * the layer kind - an entity is one or the other by definition.
 */
export function EntityChangeIcon({ change }: { change: EntityChange }) {
  return (
    <ElementIcon
      elementType={
        change.entityKind === 'section'
          ? ElementType.Section
          : ElementType.Artboard
      }
      isLayerMasked={false}
      isLayerExportable={false}
      isVariantSet={false}
    />
  )
}
